/**
 * @file derive.ts
 * @description Производные цвета оформления сайта (phase19.16 Р1; SB9 —
 * текст на кнопке, наведение, линии, фокус и служебные цвета считает
 * платформа; SB83 — текст на кнопке по контрасту, без ручного выбора).
 *
 * Производные не хранятся в документе оформления: при любой правке ролей
 * они пересчитываются, поэтому владелец не может «забыть» поправить
 * наведение под новый цвет кнопки. Одна функция работает на витрине
 * (css.ts), в кабинете (образцы в окне цвета) и в тестах.
 *
 * Служебные цвета (ошибка, успех, предупреждение) — фиксированные
 * платформенные (SB7): сообщение «Не удалось оплатить» не должно стать
 * зелёным из-за палитры магазина.
 */

import { compositeColors, contrastRatio } from "../cabinet-theme/contrast.js";
import { BLACK, mixRgb, requireHexColor, toHexColor, WHITE, withAlpha } from "./color.js";
import { colorRefHex, type SiteColorRef } from "./color-ref.js";
import { pickButtonText, resolveSurface, SITE_CONTRAST_LARGE_MIN } from "./contrast.js";
import type { SiteColors } from "./keys.js";
import { SITE_THEME_LIMITS } from "./theme-limits.js";

/** Доля затемнения при наведении («темнее на 8 %»). */
export const SITE_HOVER_SHIFT = 0.08;
/** Прозрачность линий относительно цвета текста. */
export const SITE_LINE_ALPHA = 0.15;
/**
 * Прозрачность прежней «дымки» (пятна цвета акцента по углам, SB89). С SB120
 * этапа 2 — сила свечения в теме (`backdrop.haze.strength`, %); 18 % = это.
 */
export const SITE_HAZE_ALPHA = 0.18;

/** Свечение для расчёта: сила % и цвет (нет — акцент). */
export interface SiteHazeInput {
  readonly strength: number;
  readonly color?: SiteColorRef;
}

/** Прежняя дымка: 18 % акцента — умолчание `deriveSiteColors`, если свечение не передано. */
const LEGACY_HAZE: SiteHazeInput = { strength: SITE_THEME_LIMITS.hazeStrength.legacy };

/**
 * Фиксированные служебные цвета платформы: текст сообщения и его подложка.
 * Пары подобраны с контрастом ≥ 4,5 (проверяет тест).
 */
export const SITE_STATUS_COLORS = {
  error: { fg: "#b42318", bg: "#fef3f2" },
  success: { fg: "#067647", bg: "#ecfdf3" },
  warning: { fg: "#b54708", bg: "#fffaeb" },
} as const;

export type SiteStatusKind = keyof typeof SITE_STATUS_COLORS;

/** Производные цвета — всё, чего нет в документе, но нужно витрине. */
export interface SiteDerivedColors {
  /** Текст на кнопке: `#ffffff` или тёмный (SB83). */
  buttonText: string;
  /** Кнопка при наведении. */
  buttonHover: string;
  /** Текст на кнопке при наведении (пересчитан — фон сменился). */
  buttonHoverText: string;
  /** Ссылка и акцент при наведении. */
  accentHover: string;
  /** Линии, рамки карточек и полей на светлых фонах: текст × 15 %. */
  line: string;
  /** Линии на тёмных секциях: текст на тёмном × 15 %. */
  lineOnDark: string;
  /** Кольцо фокуса: акцент, а если он сливается с фоном (< 3:1) — текст. */
  focusRing: string;
  /**
   * Пятна свечения — цвет свечения (нет — акцент) с прозрачностью силы %.
   * При силе 0 переменная не используется (`data-site-haze="false"`).
   */
  haze: string;
  status: typeof SITE_STATUS_COLORS;
}

/**
 * «Наведение темнее на 8 %»: смешение с чёрным. У почти чёрного цвета
 * затемнять некуда — наведение станет заметным только если осветлить,
 * поэтому для очень тёмных цветов (яркость < 0,05) смешиваем с белым.
 */
export function hoverColor(hex: string): string {
  const color = requireHexColor(hex);
  const opaque = color.a < 1 ? compositeColors(color, WHITE) : color;
  const nearBlack = contrastRatio(opaque, BLACK) < 1.5;
  return toHexColor(mixRgb(color, nearBlack ? WHITE : BLACK, nearBlack ? 0.12 : SITE_HOVER_SHIFT));
}

/**
 * Считает производные цвета из ролей. Цвета должны пройти схему. `haze` —
 * свечение темы (`backdrop.haze`); не передано — прежняя дымка 18 % акцента
 * (образцы цвета в кабинете, где свечение не нужно).
 */
export function deriveSiteColors(colors: SiteColors, haze: SiteHazeInput = LEGACY_HAZE): SiteDerivedColors {
  const buttonHover = hoverColor(colors.button);
  const pageBg = resolveSurface(colors, "pageBg");
  const accent = requireHexColor(colors.accent);
  const accentVisible = contrastRatio(accent, pageBg) >= SITE_CONTRAST_LARGE_MIN;
  return {
    buttonText: pickButtonText(colors).color,
    buttonHover,
    buttonHoverText: pickButtonText({ ...colors, button: buttonHover }).color,
    accentHover: hoverColor(colors.accent),
    line: toHexColor(withAlpha(requireHexColor(colors.text), SITE_LINE_ALPHA)),
    lineOnDark: toHexColor(withAlpha(requireHexColor(colors.onDark), SITE_LINE_ALPHA)),
    focusRing: accentVisible ? toHexColor(accent) : toHexColor(requireHexColor(colors.text)),
    // 18 / 100 и литерал 0,18 — одно и то же число: при 18 % и акценте строка как до этапа 2.
    haze: toHexColor(
      withAlpha(haze.color ? requireHexColor(colorRefHex(haze.color, colors)) : accent, haze.strength / 100),
    ),
    status: SITE_STATUS_COLORS,
  };
}
